/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import once from 'lodash/once';

import {
  clientErrorTracker,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';
import { ImageCarousel } from '@spotahome/ui-library';

import { OPEN_FULL_SCREEN_PHOTO_LIST } from '@spotahome/ui-library/src/tenant/components/utils/constants';

import { Mediator } from '@spotahome/soyuz-mediator';

import { SkeletonSlider } from '../Skeleton';

import { getImageTrackIndex, findImageTrack } from './utils/sliderAdapter';
import ImageMosaic from './ImageMosaic';

import './MainSliderItem.scss';

const trackEmptyImage = once(() => {
  clientErrorTracker.error({
    error: new Error('Listing images are missing'),
    source: 'Listing-MainSliderItem'
  });
});

const MainSliderItem = ({ tracks = [], onItemClick, deviceType }) => {
  const ListingSimplificationHeaderExperiment = useSoyuzExperiment(
    'ListingSimplificationHeader'
  );
  const createOpenSliderHandler = () => {
    const trackClickedId = getImageTrackIndex(tracks);

    onItemClick(trackClickedId);
  };

  const imageTrack = findImageTrack(tracks);
  const isImageTrackEmpty = !imageTrack?.items.length;

  useEffect(() => {
    if (isImageTrackEmpty) {
      trackEmptyImage();
    }
  }, [isImageTrackEmpty]);

  if (isImageTrackEmpty) {
    return <SkeletonSlider />;
  }

  const handleImageMosaicClick = () => {
    if (ListingSimplificationHeaderExperiment.get('behaviour') === 'variant') {
      Mediator.publish(OPEN_FULL_SCREEN_PHOTO_LIST);
    } else {
      createOpenSliderHandler();
    }
  };

  const hasThreeOrMoreImages = imageTrack?.items?.length >= 3;

  return deviceType === 'desktop' && hasThreeOrMoreImages ? (
    <ImageMosaic
      imageTrack={imageTrack.items}
      onClick={handleImageMosaicClick}
    />
  ) : (
    <div
      data-test="main-slider-image-container"
      className="main-slider-image ga-slider-main-image"
    >
      <ImageCarousel
        onImageClick={createOpenSliderHandler}
        items={imageTrack.items}
        chevronStyle="big"
        preloadRange={0}
        keepHeightWhileLoading
        withBackgroundChevrons
        className="main-slider-image__carousel"
      />
    </div>
  );
};

MainSliderItem.propTypes = {
  tracks: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          caption: PropTypes.string,
          src: PropTypes.string.isRequired
        })
      ),
      isHidden: PropTypes.bool
    })
  ),
  onItemClick: PropTypes.func.isRequired,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired
};

export default MainSliderItem;
